import React, { useState, useEffect } from 'react';
import { Image as ImageIcon, Play, X, ChevronRight, Flame, TrendingUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MediaItem = ({ item, onClick }) => {
  const isVideo = item.video_url || item.youtube_url || item.type === 'video';
  const thumbnailUrl = item.thumbnail_url || item.gif_url || (item.images && item.images[0]);
  
  // Extract YouTube thumbnail if it's a YouTube video
  let displayThumbnail = thumbnailUrl;
  if (item.youtube_url && !thumbnailUrl) {
    const videoId = item.youtube_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/)?.[1];
    if (videoId) {
      displayThumbnail = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
  }

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => onClick(item)}
      className="relative aspect-square rounded-xl overflow-hidden bg-gray-200 dark:bg-gray-700 group"
      data-testid={`media-item-${item.post_id}`}
    >
      {displayThumbnail ? (
        <img 
          src={displayThumbnail} 
          alt="" 
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700">
          {isVideo ? <Play className="w-8 h-8 text-white/70" /> : <ImageIcon className="w-8 h-8 text-white/70" />}
        </div>
      )}
      
      {/* Video indicator */}
      {isVideo && (
        <div className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full">
          <Play className="w-3 h-3 text-white fill-white" />
        </div>
      )}
      
      {/* Engagement overlay on hover */}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <div className="flex items-center gap-1 text-white text-sm font-bold">
          <Flame className="w-4 h-4 text-orange-400" />
          {Math.round(item.engagement_score || 0)}
        </div>
      </div>
      
      {/* User avatar */}
      <div className="absolute bottom-2 left-2">
        <Avatar className="w-6 h-6 border border-white">
          <AvatarImage src={item.user?.avatar} />
          <AvatarFallback className="bg-primary text-white text-xs">
            {item.user?.name?.charAt(0)}
          </AvatarFallback>
        </Avatar>
      </div>
    </motion.button>
  );
};

const MediaGallery = ({ isOpen, onClose, onSelectMedia, isMobile = false }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen || !isMobile) {
      fetchMedia();
    }
  }, [isOpen, isMobile]);

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API_URL}/feed/media`, {
        withCredentials: true,
      });
      setMedia(response.data);
    } catch (error) {
      console.error('Failed to load media:', error);
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          <h3 className="font-bold text-gray-900 dark:text-white">Trending Media</h3>
        </div>
        {isMobile && (
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            data-testid="close-media-gallery"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : media.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No media yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {media.map((item) => (
              <MediaItem 
                key={item.post_id} 
                item={item} 
                onClick={onSelectMedia}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Mobile: Modal
  if (isMobile) {
    return (
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/50 z-40"
            />
            {/* Modal */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="fixed left-0 top-0 bottom-0 w-80 bg-white dark:bg-gray-800 z-50 shadow-2xl"
              data-testid="mobile-media-gallery"
            >
              {content}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }

  // Desktop: Sidebar
  return (
    <div 
      className="hidden lg:block w-72 xl:w-80 h-[calc(100vh-2rem)] sticky top-4 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md rounded-3xl border border-white/20 shadow-xl overflow-hidden"
      data-testid="desktop-media-gallery"
    >
      {content}
    </div>
  );
};

// Mobile trigger button
export const MediaGalleryButton = ({ onClick }) => (
  <motion.button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className="lg:hidden fixed left-4 bottom-24 z-40 p-4 bg-gradient-to-br from-primary to-accent text-white rounded-full shadow-xl"
    data-testid="media-gallery-btn"
  >
    <ImageIcon className="w-6 h-6" />
  </motion.button>
);

export default MediaGallery;
